import { onScopeDispose, ref, watch } from 'vue'
import { registerApi } from '../services/registerApi.js'

const DEBOUNCE_MS = 600
const NPI_PATTERN = /^\d{10}$/
export const NPI_FORMAT_MESSAGE = 'NPI must contain exactly 10 digits.'

export function useNpiLookup(npiRef, onFound, onClear) {
  const isLookingUp = ref(false)
  const npiResult   = ref(null)   // full provider object from the API
  const npiError    = ref('')
  let timer = null
  let lookupSequence = 0

  async function lookup(npi) {
    const sequence = ++lookupSequence

    isLookingUp.value = true
    npiError.value    = ''
    npiResult.value   = null

    try {
      const { data } = await registerApi.lookupNpi(npi)

      if (sequence !== lookupSequence) return

      if (data.found) {
        npiResult.value = data
        onFound?.(data)
      } else {
        npiError.value = data.message ?? 'No provider found for this NPI number.'
        onClear?.()
      }
    } catch {
      if (sequence !== lookupSequence) return

      npiError.value = 'Could not reach the NPI Registry. Please try again.'
      onClear?.()
    } finally {
      if (sequence === lookupSequence) {
        isLookingUp.value = false
      }
    }
  }

  const stopWatch = watch(npiRef, (value) => {
    clearTimeout(timer)
    lookupSequence += 1

    const npi = String(value ?? '')
    npiResult.value = null
    isLookingUp.value = false
    onClear?.()

    if (!npi) {
      npiError.value = ''
      return
    }

    if (!NPI_PATTERN.test(npi)) {
      npiError.value = NPI_FORMAT_MESSAGE
      return
    }

    npiError.value = ''
    timer = setTimeout(() => lookup(npi), DEBOUNCE_MS)
  })

  onScopeDispose(() => {
    clearTimeout(timer)
    stopWatch()
  })

  return { isLookingUp, npiResult, npiError }
}
